import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { ENV_CONFIG } from '@/config/env.example';
import { STORAGE_KEYS } from '@/lib/security';

// Shared interfaces
interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retryCount?: number;
  _queued?: boolean;
}

interface ExtendedAxiosError extends Error {
  config?: ExtendedAxiosRequestConfig;
  response?: {
    status?: number;
    data?: {
      message?: string;
      code?: string;
    };
  };
}

// Queue for failed requests during token refresh
interface QueuedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
  config: ExtendedAxiosRequestConfig;
}

let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];
// SECURITY: Promise cache to prevent race conditions in token refresh
let refreshTokenPromise: Promise<string | null> | null = null;

/**
 * Process queued requests after token refresh
 */
const processQueue = (error: unknown = null, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      if (prom.config.headers && token) {
        prom.config.headers.Authorization = `Bearer ${token}`;
      }
      prom.resolve(prom.config);
    }
  });

  failedQueue = [];
};

/**
 * Creates an identity API client without interceptors
 * Used for authentication endpoints that don't require a token
 */
const createIdentityClient = () => {
  const client: AxiosInstance = axios.create({
    baseURL: ENV_CONFIG.IDENTITY_URL,
  });

  return {
    get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.get<T>(url, config);
      return response.data;
    },
    post: async <T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.post<T>(url, data, config);
      return response.data;
    },
    put: async <T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.put<T>(url, data, config);
      return response.data;
    },
    patch: async <T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.patch<T>(url, data, config);
      return response.data;
    },
    delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.delete<T>(url, config);
      return response.data;
    },
  };
};

/**
 * Creates the main API client with authentication interceptors
 * Automatically adds JWT token to requests and handles 401 errors
 */
const createClient = () => {
  const client: AxiosInstance = axios.create({
    baseURL: ENV_CONFIG.API_URL,
  });

  const onRequestSuccess = (config: InternalAxiosRequestConfig) => {
    // Check both sessionStorage and localStorage for access token
    // SessionStorage is checked first (for non-rememberMe sessions)
    const token = sessionStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) || 
                  localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };

  const onRequestError = (error: Error) => {
    return Promise.reject(error);
  };

  const onResponseSuccess = (response: AxiosResponse) => {
    return response;
  };

  const onResponseError = async (error: ExtendedAxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    // Handle 401 Unauthorized - attempt token refresh
    if (error.response?.status === 401 && originalRequest && !originalRequest._retryCount) {
      // Mark this request as retried immediately to prevent infinite loops
      originalRequest._retryCount = 1;

      // SECURITY FIX: Use promise caching to prevent race conditions
      // If already refreshing, wait for the existing refresh to complete
      if (isRefreshing && refreshTokenPromise) {
        try {
          const newToken = await refreshTokenPromise;
          if (newToken && originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return client.request(originalRequest);
          }
          return Promise.reject(error);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      // Start refreshing - set flag and create promise
      isRefreshing = true;
      
      // Create a single promise for the refresh operation
      refreshTokenPromise = (async () => {
        try {
          // Import authService dynamically to avoid circular dependency
          const { authService } = await import('@/services/Auth/auth.service');
          
          // Attempt to refresh the token
          const newToken = await authService.refreshAccessToken();

          if (newToken) {
            // Update the failed request's authorization header
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
            }

            // Process all queued requests with the new token
            processQueue(null, newToken);

            return newToken;
          } else {
            // Refresh failed, clear queue and logout
            processQueue(new Error('Token refresh failed'), null);

            // Clear all tokens
            localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
            localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
            sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
            sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

            // Redirect to login
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
              window.location.href = '/login';
            }

            return null;
          }
        } catch (refreshError) {
          // Refresh failed, clear queue
          processQueue(refreshError, null);

          // Clear tokens and redirect
          localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME);
          sessionStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
          sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);

          if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
            window.location.href = '/login';
          }

          throw refreshError;
        } finally {
          // Reset state after refresh completes (success or failure)
          isRefreshing = false;
          refreshTokenPromise = null;
        }
      })();

      try {
        const newToken = await refreshTokenPromise;
        if (newToken) {
          // Retry the original request with new token
          return client.request(originalRequest);
        }
        return Promise.reject(error);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    // For other errors, just reject
    return Promise.reject({
      message: error.response?.data?.message || error.message || 'An error occurred',
      status: error.response?.status || 500,
      code: error.response?.data?.code,
    });
  };

  client.interceptors.request.use(onRequestSuccess, onRequestError);
  client.interceptors.response.use(onResponseSuccess, onResponseError);

  return {
    get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.get<T>(url, config);
      return response.data;
    },
    post: async <T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.post<T>(url, data, config);
      return response.data;
    },
    put: async <T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.put<T>(url, data, config);
      return response.data;
    },
    patch: async <T, D = unknown>(url: string, data?: D, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.patch<T>(url, data, config);
      return response.data;
    },
    delete: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> => {
      const response = await client.delete<T>(url, config);
      return response.data;
    },
  };
};

// Create and export both clients
export const identityClient = createIdentityClient();
export const apiClient = createClient();

// Keep default export for backward compatibility
const api = apiClient;
export default api;
export { api };

